"use client";

import { useContext, useSyncExternalStore, useCallback, useRef, useMemo } from "react";
import { EditorContext } from "./context";
import type { EditorState, EditorActions, QueryMethods } from "../state/types";
import { isValidElement } from "react";

type EditorSelector<T> = (state: EditorState, query: QueryMethods) => T;

interface EditorConnectors {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  create: (el: HTMLElement | null, element: any) => HTMLElement | null;
}

interface UseEditorReturn {
  actions: EditorActions;
  query: QueryMethods;
  connectors: EditorConnectors;
}

const EMPTY_OBJ = Object.freeze({}) as Record<string, never>;
const NOOP_UNSUBSCRIBE = () => {};
const NOOP_SUBSCRIBE = () => NOOP_UNSUBSCRIBE;

export function useEditor(): UseEditorReturn;
export function useEditor<T>(
  selector: EditorSelector<T>,
): UseEditorReturn & T;
export function useEditor<T>(
  selector?: EditorSelector<T>,
): UseEditorReturn & T {
  const ctx = useContext(EditorContext);
  if (!ctx) {
    throw new Error("useEditor must be used within an <Editor> component");
  }

  const { store, actions, query } = ctx;
  const hasSelector = typeof selector === "function";

  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const cachedRef = useRef<T>(EMPTY_OBJ as T);
  const previousStateRef = useRef<EditorState | null>(null);
  const previousSelectorRef = useRef<EditorSelector<T> | undefined>(selector);

  const getSnapshot = useCallback(() => {
    if (!hasSelector || !selectorRef.current) {
      return cachedRef.current;
    }

    const selectorFn = selectorRef.current;
    const state = store.getState();
    if (
      previousStateRef.current === state &&
      previousSelectorRef.current === selectorFn
    ) {
      return cachedRef.current;
    }

    const next = selectorFn(state, query);
    previousStateRef.current = state;
    previousSelectorRef.current = selectorFn;

    if (shallowEqual(cachedRef.current, next)) {
      return cachedRef.current;
    }
    cachedRef.current = next;
    return next;
  }, [store, query, hasSelector]);

  const selected = useSyncExternalStore(
    hasSelector ? store.subscribe : NOOP_SUBSCRIBE,
    getSnapshot,
    getSnapshot,
  );

  // Track cleanup for create connectors
  const createCleanupMapRef = useRef(new WeakMap<HTMLElement, () => void>());

  const connectors: EditorConnectors = useMemo(() => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    create(el: HTMLElement | null, element: any) {
      if (!el) return el;

      // Clean up previous listeners on this element
      const prevCleanup = createCleanupMapRef.current.get(el);
      prevCleanup?.();

      el.setAttribute("draggable", "true");

      const dragStartHandler = (e: DragEvent) => {
        if (!store.getState().options.enabled) return;
        if (isValidElement(element)) {
          e.dataTransfer?.setData(
            "application/builder-create",
            "true",
          );
          if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = "copyMove";
          }
          // Store element reference globally so drop targets can read it.
          (globalThis as { __builderCreateElement?: unknown }).__builderCreateElement = element;
        }
      };

      const dragEndHandler = () => {
        delete (globalThis as { __builderCreateElement?: unknown }).__builderCreateElement;
        hideDropIndicator();
      };

      el.addEventListener("dragstart", dragStartHandler);
      el.addEventListener("dragend", dragEndHandler);

      createCleanupMapRef.current.set(el, () => {
        el.removeEventListener("dragstart", dragStartHandler);
        el.removeEventListener("dragend", dragEndHandler);
      });

      return el;
    },
  }), [store]);

  return {
    ...selected,
    actions,
    query,
    connectors,
  } as UseEditorReturn & T;
}

function hideDropIndicator() {
  if (typeof document === "undefined") return;
  const marker = document.getElementById("__builder-drop-indicator");
  if (marker) {
    (marker as HTMLDivElement).style.display = "none";
  }
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;

  if (typeof a !== "object" || a === null || typeof b !== "object" || b === null) {
    return false;
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i += 1) {
      if (!Object.is(a[i], b[i])) return false;
    }
    return true;
  }

  const objA = a as Record<string, unknown>;
  const objB = b as Record<string, unknown>;
  const keysA = Object.keys(objA);
  if (keysA.length !== Object.keys(objB).length) {
    return false;
  }

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(objB, key)) {
      return false;
    }
    if (!Object.is(objA[key], objB[key])) {
      return false;
    }
  }
  return true;
}
